import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { StockMaxMinImportModel } from 'src/app/models/imports/StockMaxMinImportModel';
import { StockMaxMinModel } from 'src/app/models/inventory/stock-max-min.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StockMaxMinService {

  constructor(private _httpClient: HttpClient) { }

  createStockMaxMin(stockMaxMinContract: StockMaxMinModel): Observable<StockMaxMinModel> {
    return this._httpClient.post<StockMaxMinModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.CreateStockMaxMin}`,
      stockMaxMinContract);
  }

  deleteStockMaxMin(stockMaxMinId: number): Observable<StockMaxMinModel> {
    return this._httpClient.delete<StockMaxMinModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.DeleteStockMaxMin}/${stockMaxMinId}`);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.ExportGeneralConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.ExportImportErrors}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  getAllStockMaxMinWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.GetAllStockMaxMinWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getArticlesForSelect(inventoryId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ArticleSelectionTableModel>> {
    const params = new HttpParams().set('inventoryId', inventoryId).set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<ArticleSelectionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.GetArticlesForSelectAsync}`,
      filters, { params: params }
    );
  }

  getArticlesForSelectByInventoryIdAsync(inventoryId: number): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.GetArticlesForSelectByInventoryIdAsync}/${inventoryId}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.GetColumnsForGeneralConsultationAsync}`);
  }

  getFirstStockMaxMin(): Observable<StockMaxMinModel> {
    return this._httpClient.get<StockMaxMinModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.GetFirstStockMaxMin}`);
  }

  getLastStockMaxMin(): Observable<StockMaxMinModel> {
    return this._httpClient.get<StockMaxMinModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.GetLastStockMaxMin}`);
  }

  getNextStockMaxMinByKeysAsync(stockMaxMinId: number): Observable<StockMaxMinModel> {
    const params = new HttpParams().set('stockMaxMinId', stockMaxMinId);
    return this._httpClient.get<StockMaxMinModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.GetNextStockMaxMinByKeysAsync}`,
      { params: params }
    );
  }

  getPreviousStockMaxMinByKeysAsync(stockMaxMinId: number): Observable<StockMaxMinModel> {
    const params = new HttpParams().set('stockMaxMinId', stockMaxMinId);
    return this._httpClient.get<StockMaxMinModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.GetPreviousStockMaxMinByKeysAsync}`,
      { params: params }
    );
  }

  getStockMaxMinById(stockMaxMinId: number): Observable<StockMaxMinModel> {
    return this._httpClient.get<StockMaxMinModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.GetStockMaxMinById}/${stockMaxMinId}`);
  }

  importStockMaxMin(stockMaxMinImports: Array<StockMaxMinImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.ImportStockMaxMin}`, stockMaxMinImports);
  }

  updateStockMaxMin(stockMaxMinModel: StockMaxMinModel): Observable<StockMaxMinModel> {
    return this._httpClient.put<StockMaxMinModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.UpdateStockMaxMin}`,
      stockMaxMinModel);
  }

  getStockMaxMinByKeysAsync(inventoryId: number, branchId: number, storeId: number, articleId: number): Observable<StockMaxMinModel> {
    const params = new HttpParams()
      .set('inventoryId', inventoryId)
      .set('branchId', branchId)
      .set('storeId', storeId)
      .set('articleId', articleId)

    return this._httpClient.get<StockMaxMinModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.stockMaxMin.GetStockMaxMinByKeys}`,
      { params });
  }
}
